import React, { useCallback, useEffect, useState } from "react";
import { Input } from "@/app/global-components/input";
import { MenuItem, OutlinedInput, Select, Tooltip } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { RootState } from "@/store/type";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { debounce, isEmpty, isNull } from "lodash";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { routes } from "@/consts";
import ProductCard from "@/pages/articles/components/ProductCard";
import AddNewItem from "@/pages/articles/components/AddnewItem";
import useRegisterCompany from "@/hooks/use-register-company";
import useItems from "@/hooks/use-item";
import { showToast } from "@/app/global-components/toaster";
import useOffer from "@/hooks/useTender";
import { LANG } from "@/types";

export interface CategoryOption {
  value: any;
  label: any;
}
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const ArticlesList: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [Categoris, setCategoris] = useState<CategoryOption[]>([]);
  const [filterCategories, setFilterCategories] = useState<string[] | string>(
    []
  );

  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const {
    fetchItems,
    fetchItemSearch,
    saveItem,
    deleteItem,
    isLoading,
    error,
  } = useItems();
  const navigate = useNavigate();

  const { getCompanyMine } = useRegisterCompany();
  const { getcategories } = useOffer();
  useEffect(() => {
    const fetching = async () => {
      const Categories = await getcategories();
      if (Categories) {
        const uniqueCategories = Categories.data.filter(
          (item: { name: any }, index: any, self: any[]) =>
            index === self.findIndex((t) => t.name === item.name)
        );
        const Cates: CategoryOption[] = uniqueCategories.map(
          (item: { id: any; name: any }) => ({
            value: item.id,
            label: item.name,
          })
        );
        setCategoris(Cates);
      } else {
        setCategoris([]);
      }
      const res = await fetchItemSearch(searchTerm, myCompany.id);
      if (res) {
        setItems(res.data);
      }
    };
    fetching();
  }, [searchTerm]);
  const handleAddItem = async (newItem: any) => {
    const response = await saveItem({
      ...newItem,
      ItemNR: parseInt(isNull(myCompany.itemNr) ? 0 : myCompany.itemNr) + 1,
    });
    if (response) {
      showToast(t("alerts.success.itemSaved"), "success");
      setItems((prev) => [...prev, response.data]);
    }
  };
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      setSearchTerm(searchTerm);
    }, 500),
    []
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedSearch(value);
  };
  const handleDeleteItem = async (id: string) => {
    const confirmed = window.confirm(t("alerts.confirmation.deleteItem"));
    if (confirmed) {
      const response = await deleteItem(id);
      if (response) {
        showToast(t("alerts.success.itemDeleted"), "success");
        setItems((prev) => prev.filter((item) => item.id !== id));
      }
    }
  };

  const handleViewItem = (id: string) => {
    navigate(`${routes.items}/${id}`);
  };

  useEffect(() => {
    if (!isEmpty(myCompany)) {
      const loadItems = async () => {
        const items = await fetchItems(myCompany.id);
        setItems(items.data);
      };
      if (!isNull(myCompany)) {
        loadItems();
      }
    }
  }, [myCompany]);
  useEffect(() => {
    getCompanyMine();
  }, []);
  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ background: "#f8fafa" }}
    >
      {/* <NavPage isForm={0} /> */}
      {/*!isNull(myCompany)?<CompanyWarningPopup open={true}/>:""*/}

      <div
        className="container mx-auto px-4 py-8"
        style={{ overflow: "auto", paddingTop: 30 }}
      >
        <div className="mb-6 flex justify-between items-right">
          <h2 className="text-3xl font-semibold tracking-tight mb-2 text-primary-dark">
            {t("products.title")}
          </h2>
          <Tooltip title="Add New Item">
            <AddCircleOutlineIcon
              fontSize="large"
              className="cursor-pointer text-primary-dark"
              onClick={() => setIsModalOpen(true)}
            />
          </Tooltip>
        </div>
        <div className="flex justify-between flex-col md:flex-row md:items-center mb-6 gap-4">
          <Input
            type="text"
            placeholder={t("actions.search")}
            className="focus-visible:ring-secondary-brightYellow bg-white w-96 py-2 px-3 text-xs flex items-center md:text-base"
            onChange={handleChange}
          />
          <Select
            displayEmpty
            id="categorie-multiple-name"
            input={<OutlinedInput />}
            inputProps={{ "aria-label": "Without label" }}
            className="h-10 bg-white w-36 md:w-64 xl:w-80 border !border-zinc-500"
            sx={{
              "& .MuiSelect-select": {
                paddingRight: "10px !important",
              },
              "& .MuiSelect-icon": {
                left: locale === LANG.EN ? "unset" : 0,
                marginLeft: 0,
                right: locale === LANG.EN ? 0 : "unset",
              },
            }}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <em>{t("categories.select")}</em>;
              }
              const value = Categoris.filter((value) =>
                selected.includes(value.value)
              ).map((item) => item.label);
              return value.join(", ");
            }}
            multiple
            value={filterCategories}
            onChange={(e) => {
              setFilterCategories(e.target.value);
            }}
            MenuProps={MenuProps}
          >
            <MenuItem disabled value="">
              <em>{t("categories.select")}</em>
            </MenuItem>
            {Categoris.map((name) => (
              <MenuItem
                className="!mb-2"
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "#000",
                    borderRadius: 15,
                    color: "#fff",
                    fontWeight: "600",
                  },
                  "&:hover.MuiMenuItem-root": {
                    backgroundColor: "#000",
                    borderRadius: 15,
                    color: "#fff",
                  },
                }}
                key={name.value}
                value={name.value}
                // style={getStyles(name, personName, theme)}
              >
                <span>{name.label}</span>
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items
            .filter(
              (item) =>
                item.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
                (!filterCategories.length ||
                  filterCategories.includes(item.categories))
            )
            .map((item, index) => (
              <ProductCard
                key={index}
                item={item}
                onDelete={handleDeleteItem}
                onView={handleViewItem}
              />
            ))}
        </div>
      </div>

      <AddNewItem
        Categoris={Categoris}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleAddItem}
        isLoading={isLoading}
        error={error}
      />
    </div>
  );
};

export default ArticlesList;
